import { useEffect, useState } from "react";

import { useGetAuthUser } from "@/api/get-user";
import { useVidyoStore } from "@/features/vidyo-call/utils/vidyo-store";
import socketClient from "@/lib/socket-io";
import { AuthErrorPayload } from "@classview/shared-types";

type ConnectToSocketProps = {
  enabled?: boolean;
};

type ConnectToSocketReturn = {
  isSocketConnected: boolean;
};

export function useConnectToSocket({
  enabled = true,
}: ConnectToSocketProps = {}): ConnectToSocketReturn {
  const [isSocketConnected, setIsSocketConnected] = useState(false);
  const [isMounted, setIsMounted] = useState(false);
  const [retryCount, setRetryCount] = useState(0);

  const isWebSocketAuthenticated = useVidyoStore(
    (state) => state.webSocket.isAuthenticated,
  );
  const setIsWebSocketAuthenticated = useVidyoStore(
    (state) => state.webSocket.setIsAuthenticated,
  );
  const setWebSocketAuthError = useVidyoStore(
    (state) => state.webSocket.setAuthError,
  );
  const authError = useVidyoStore((state) => state.webSocket.authError);
  const { data: user, isSuccess, refetch } = useGetAuthUser();

  /**
   * This is used to check if the component is mounted.
   * This is used to prevent the useEffect from running when the component is not mounted.
   * Otherwise socketClient would connect when the component is not mounted.
   */
  useEffect(() => {
    setIsMounted(true);

    // Only initialize socket connection when component is mounted and we have a user token
    if (!socketClient.connected && enabled) {
      socketClient.connect();
    }

    return () => {
      setIsMounted(false);
      socketClient.disconnect();
    };
  }, [enabled]);

  /**
   * This is used to handle the connection and disconnection events of the socket client
   */
  useEffect(() => {
    if (!isMounted) {
      return;
    }

    const handleConnect = () => {
      setIsSocketConnected(true);
      console.log("[useConnectToSocket] connected");
      if (socketClient.recovered) {
        console.log("[useConnectToSocket] recovered");
      }
    };

    const handleDisconnect = (reason: string) => {
      setIsSocketConnected(false);
      setWebSocketAuthError(null);
      setIsWebSocketAuthenticated(false);
      console.log("[useConnectToSocket] disconnected", reason);
    };

    const handleConnectError = (error: Error) => {
      if (socketClient.active) {
        console.log("socketClient.active", error);
        console.log(
          "[useConnectToSocket] temporary failure, the socket will automatically try to reconnect",
          error,
        );
      } else {
        // the connection was denied by the server
        // in that case, `socket.connect()` must be manually called in order to reconnect
        console.log("[useConnectToSocket] connect_error", error.message);
      }
    };

    socketClient.on("connect", handleConnect);
    socketClient.on("disconnect", handleDisconnect);
    socketClient.on("connect_error", handleConnectError);
    return () => {
      socketClient.off("connect", handleConnect);
      socketClient.off("connect_error", handleConnectError);
      socketClient.off("disconnect", handleDisconnect);
    };
    //TODO: session.data?.id?
  }, [isMounted, enabled, setWebSocketAuthError, setIsWebSocketAuthenticated]);

  // Attach auth listeners only once
  useEffect(() => {
    const handleAuthError = (error: AuthErrorPayload) => {
      console.log("[useConnectToSocket] authError", error);
      setWebSocketAuthError(error);
      setIsWebSocketAuthenticated(false);
    };
    const handleAuthenticated = () => {
      console.log("[useConnectToSocket] authenticated");
      setIsWebSocketAuthenticated(true);
    };

    socketClient.on("authError", handleAuthError);
    socketClient.on("authenticated", handleAuthenticated);

    return () => {
      socketClient.off("authError", handleAuthError);
      socketClient.off("authenticated", handleAuthenticated);
    };
  }, [setWebSocketAuthError, setIsWebSocketAuthenticated]);

  // Only emit authenticate when connected and not already authenticated
  useEffect(() => {
    if (
      isSocketConnected &&
      isSuccess &&
      !isWebSocketAuthenticated &&
      retryCount < 3
    ) {
      socketClient.emit("authenticate", {
        token: user?.token || "invalid-token-will-throw-auth-error",
      });
    }
  }, [
    isSocketConnected,
    isSuccess,
    user?.token,
    isWebSocketAuthenticated,
    retryCount,
  ]);

  //try to reconnect if auth error, but only up to 3 times
  useEffect(() => {
    const handleAuthErrorWithRetry = async () => {
      if (isSocketConnected && authError) {
        // refetch the user to get a new token
        await refetch();
        if (retryCount < 3) {
          setRetryCount((prev) => prev + 1);
        } else {
          console.log("[useConnectToSocket] retry-auth-error", authError);
        }
      }
    };
    handleAuthErrorWithRetry();
  }, [isSocketConnected, authError, user?.token, retryCount, refetch]);

  // Reset store and retry count on unmount
  useEffect(() => {
    return () => {
      setIsWebSocketAuthenticated(false);
      setWebSocketAuthError(null);
      setRetryCount(0);
    };
  }, [setIsWebSocketAuthenticated, setWebSocketAuthError]);

  return {
    isSocketConnected,
  };
}
